import { SearchResultsError } from './searchResultsError'
import { router } from '@inertiajs/svelte'
import type { LiteClient } from 'algoliasearch/lite'
import { debounce, latestWins } from '@/utils/debouncedSearch'
import {
  mergeSearchResults,
  searchFinishedWithNoResults,
  takeTopAlgoliaHits,
  type ExternalBook,
  type SearchHit,
} from '@/utils/searchRanking'
import { externalSelectPayload } from '@/utils/globalBookSearch'
import { searchBooks } from '@/utils/searchRequests'
import { select_search_books_path } from '@/routes'

const SEARCH_API_KEY = '8a8edf374e4a57f0eeb85ad3de0b2dc8'

type SearchConfig = {
  applicationId: () => string
  indexName: () => string
}

type SearchDependencies = {
  createClient: (applicationId: string) => Promise<LiteClient>
  searchBooks: typeof searchBooks
  visit: (url: string) => void
  post: (
    url: string,
    data: ReturnType<typeof externalSelectPayload>,
    options: { onSuccess: () => void }
  ) => void
}

const defaultDependencies: SearchDependencies = {
  async createClient(applicationId) {
    const { liteClient } = await import('algoliasearch/lite')
    return liteClient(applicationId, SEARCH_API_KEY)
  },
  searchBooks,
  visit: (url) => router.visit(url),
  post: (url, data, options) => router.post(url, data, options),
}

export function createGlobalSearch(
  config: SearchConfig,
  dependencies: SearchDependencies = defaultDependencies
) {
  return new GlobalSearchController(config, dependencies)
}

class GlobalSearchController {
  showSearch = $state(false)
  query = $state('')
  results = $state<SearchHit[]>([])
  books = $state<ExternalBook[]>([])
  isSearching = $state(false)
  isSearchingBooks = $state(false)
  bookSearchError = $state('')
  isAlgoliaQueued = $state(false)
  isBooksQueued = $state(false)

  private client: LiteClient | null = null
  private clientPromise: Promise<LiteClient> | null = null
  private booksController: AbortController | null = null
  private readonly algoliaGuard = latestWins()
  private readonly algoliaSearch
  private readonly bookSearch

  constructor(
    private readonly config: SearchConfig,
    private readonly dependencies: SearchDependencies
  ) {
    this.algoliaSearch = debounce(() => {
      this.isAlgoliaQueued = false
      void this.search(this.query)
    }, 200)
    this.bookSearch = debounce(() => {
      this.isBooksQueued = false
      void this.searchBooks(this.query)
    }, 0)
  }

  get mergedResults() {
    return mergeSearchResults(this.results, this.books)
  }

  get hasNoResults() {
    return (
      !this.bookSearchError &&
      searchFinishedWithNoResults({
        query: this.query,
        resultCount: this.mergedResults.length,
        isSearching: this.isSearching,
        isSearchingBooks: this.isSearchingBooks,
        isSearchPending: this.isAlgoliaQueued || this.isBooksQueued,
      })
    )
  }

  open() {
    this.showSearch = true
    void this.loadClient().catch(() => {})
  }

  close() {
    this.showSearch = false
    this.query = ''
    this.resetResults()
  }

  destroy() {
    this.resetResults()
  }

  resetResults() {
    this.algoliaSearch.cancel()
    this.bookSearch.cancel()
    this.algoliaGuard.next()
    this.booksController?.abort()
    this.booksController = null
    this.results = []
    this.books = []
    this.bookSearchError = ''
    this.isSearching = false
    this.isSearchingBooks = false
    this.isAlgoliaQueued = false
    this.isBooksQueued = false
  }

  handleInput() {
    this.resetResults()
    const shouldSearch = this.query.length >= 2
    this.isAlgoliaQueued = shouldSearch
    this.isBooksQueued = shouldSearch
    this.algoliaSearch.run()
    this.bookSearch.run()
  }

  selectResult(hit: SearchHit) {
    this.close()
    this.dependencies.visit(hit.url)
  }

  selectExternal(book: ExternalBook) {
    this.dependencies.post(
      select_search_books_path(),
      externalSelectPayload(book),
      { onSuccess: () => this.close() }
    )
  }

  private loadClient(): Promise<LiteClient> {
    this.clientPromise ??= this.dependencies
      .createClient(this.config.applicationId())
      .then((client) => (this.client = client))
      .catch((error) => {
        this.clientPromise = null
        throw error
      })
    return this.clientPromise
  }

  private async searchOtherRecords(query: string): Promise<SearchHit[]> {
    const client = this.client ?? (await this.loadClient())
    const response = await client.search<SearchHit>({
      requests: [
        {
          indexName: this.config.indexName(),
          query,
          hitsPerPage: 20,
          filters: 'NOT record_type:book',
        },
      ],
    })
    const firstResult = response.results[0]
    return takeTopAlgoliaHits(
      firstResult && 'hits' in firstResult ? firstResult.hits : []
    )
  }

  private async search(query: string) {
    const requestId = this.algoliaGuard.next()
    if (query.length < 2) {
      this.results = []
      this.isSearching = false
      return
    }
    this.isSearching = true
    const current = () =>
      !this.algoliaGuard.isStale(requestId) &&
      query === this.query &&
      this.showSearch
    try {
      const hits = await this.searchOtherRecords(query)
      if (current()) this.results = hits
    } catch {
      if (current()) this.results = []
    } finally {
      if (!this.algoliaGuard.isStale(requestId)) this.isSearching = false
    }
  }

  private async searchBooks(query: string) {
    this.booksController?.abort()

    if (query.length < 2) {
      this.booksController = null
      this.books = []
      this.isSearchingBooks = false
      return
    }

    const controller = new AbortController()
    this.booksController = controller
    this.isSearchingBooks = true

    try {
      const current = () =>
        !controller.signal.aborted && query === this.query && this.showSearch
      const books = await this.dependencies.searchBooks(
        query,
        controller.signal,
        (partial) => {
          if (current()) this.books = partial
        }
      )
      if (controller.signal.aborted || query !== this.query || !this.showSearch)
        return
      this.books = books
    } catch (error) {
      if (
        !controller.signal.aborted &&
        query === this.query &&
        this.showSearch
      ) {
        this.bookSearchError =
          error instanceof SearchResultsError
            ? error.message
            : 'Book search failed. Please try again.'
        if (error instanceof SearchResultsError)
          this.books = error.results as ExternalBook[]
      }
    } finally {
      if (this.booksController === controller) {
        this.isSearchingBooks = false
      }
    }
  }
}
